import { useMemo } from 'react';

import {
  isValidDecimalInput,
  normalizeDecimalInput,
  redistributePercentages,
} from '@splitfiesta/core';
import {
  SplitMethod,
  getCurrencyDecimals,
  roundToCurrencyPrecision,
} from '@splitfiesta/shared';

import { Split, SplitCalculationResult } from './types';

export function useSplitCalculation(
  splits: Split[],
  setSplits: (splits: Split[]) => void,
  splitMethod: SplitMethod,
  totalAmount: number,
  currency: string
) {
  const numericAmount = totalAmount || 0;
  const includedSplits = splits.filter(s => s.included);

  // Calculate equal split amounts with remainder going to last person
  const getEqualSplitAmount = (userId: string): number => {
    if (splitMethod !== 'equal' || includedSplits.length === 0) return 0;

    const baseAmount = roundToCurrencyPrecision(
      numericAmount / includedSplits.length,
      currency
    );

    // Check if this is the last included person
    const lastIncludedUser = includedSplits[includedSplits.length - 1];
    if (userId === lastIncludedUser.userId) {
      // Calculate what others will pay
      const othersTotal = baseAmount * (includedSplits.length - 1);
      // Last person pays the remainder
      return roundToCurrencyPrecision(numericAmount - othersTotal, currency);
    }

    return baseAmount;
  };

  // Calculate shares split amounts with remainder going to last person
  const getSharesSplitAmount = (userId: string): number => {
    if (splitMethod !== 'shares' || includedSplits.length === 0) return 0;

    // Calculate total shares
    const totalShares = includedSplits.reduce(
      (sum, s) => sum + (s.shares ?? s.defaultShares ?? 1),
      0
    );

    if (totalShares === 0) return 0;

    const split = includedSplits.find(s => s.userId === userId);
    if (!split) return 0;

    const userShares = split.shares ?? split.defaultShares ?? 1;
    const baseAmount = roundToCurrencyPrecision(
      (userShares / totalShares) * numericAmount,
      currency
    );

    // Check if this is the last included person - they get the remainder
    const lastIncludedUser = includedSplits[includedSplits.length - 1];
    if (userId === lastIncludedUser.userId) {
      // Calculate what others will pay
      const othersTotal = includedSplits
        .filter(s => s.userId !== userId)
        .reduce((sum, s) => {
          const shares = s.shares ?? s.defaultShares ?? 1;
          return (
            sum +
            roundToCurrencyPrecision(
              (shares / totalShares) * numericAmount,
              currency
            )
          );
        }, 0);
      // Last person pays the remainder
      return roundToCurrencyPrecision(numericAmount - othersTotal, currency);
    }

    return baseAmount;
  };

  // Get effective shares for a user (per-expense override or default)
  const getEffectiveShares = (userId: string): number => {
    const split = splits.find(s => s.userId === userId);
    return split?.shares ?? split?.defaultShares ?? 1;
  };

  const calculationResult: SplitCalculationResult = useMemo(() => {
    // Calculate total shares for shares method
    const totalShares = includedSplits.reduce(
      (sum, s) => sum + (s.shares ?? s.defaultShares ?? 1),
      0
    );

    // Calculate total allocated based on split method
    const getTotalAllocated = (): number => {
      if (splitMethod === 'equal' || splitMethod === 'shares') {
        return numericAmount; // Equal and shares split always allocate full amount
      } else if (splitMethod === 'percentage') {
        return includedSplits.reduce(
          (sum, split) => sum + ((split.percentage || 0) / 100) * numericAmount,
          0
        );
      } else {
        return includedSplits.reduce(
          (sum, split) => sum + (split.amount || 0),
          0
        );
      }
    };

    const totalAllocated = getTotalAllocated();
    const remaining = numericAmount - totalAllocated;
    const isFullyAllocated = Math.abs(remaining) < 0.01;
    const totalPercentage = includedSplits.reduce(
      (sum, split) => sum + (split.percentage || 0),
      0
    );

    const canAutoComplete = (() => {
      if (splitMethod === 'percentage') {
        return totalPercentage < 100;
      }
      if (splitMethod === 'custom') {
        return totalAllocated < numericAmount;
      }
      return false;
    })();

    return {
      totalAllocated,
      remaining,
      isFullyAllocated,
      totalPercentage,
      totalShares,
      canAutoComplete,
    };
  }, [splitMethod, numericAmount, includedSplits]);

  const handleIncludeToggle = (userId: string) => {
    const newSplits = splits.map(split =>
      split.userId === userId
        ? {
            ...split,
            included: !split.included,
            // Reset values when unchecking
            amount: !split.included ? split.amount : undefined,
            amountFormatted: !split.included
              ? split.amountFormatted
              : undefined,
            percentage: !split.included ? split.percentage : undefined,
            shares: !split.included ? split.shares : undefined,
          }
        : split
    );
    setSplits(newSplits);
  };

  const handleSharesChange = (userId: string, value: string) => {
    const shares = value === '' ? undefined : parseInt(value, 10);
    setSplits(
      splits.map(split =>
        split.userId === userId
          ? {
              ...split,
              shares:
                shares !== undefined && !isNaN(shares) && shares >= 1
                  ? Math.min(shares, 100)
                  : undefined,
            }
          : split
      )
    );
  };

  const handlePercentageChange = (userId: string, value: string) => {
    if (!isValidDecimalInput(value)) {
      return;
    }
    const parsed = normalizeDecimalInput(value);
    const percentage = value === '' || isNaN(parsed) ? 0 : parsed;
    setSplits(
      splits.map(split =>
        split.userId === userId
          ? {
              ...split,
              percentage,
            }
          : split
      )
    );
  };

  // Handle percentage slider change - redistributes others proportionally to keep total at 100%
  const handlePercentageSliderChange = (userId: string, value: number) => {
    const currentPercentages: Record<string, number> = {};
    for (const s of includedSplits) {
      currentPercentages[s.userId] = s.percentage || 0;
    }

    const redistributed = redistributePercentages(
      userId,
      value,
      currentPercentages
    );

    setSplits(
      splits.map(split =>
        split.included && redistributed[split.userId] !== undefined
          ? { ...split, percentage: redistributed[split.userId] }
          : split
      )
    );
  };

  const handleAmountChange = (userId: string, value: string | undefined) => {
    setSplits(
      splits.map(split =>
        split.userId === userId
          ? {
              ...split,
              amount: value ? normalizeDecimalInput(value) : undefined,
              amountFormatted: value,
            }
          : split
      )
    );
  };

  const handleSplitEvenly = () => {
    const includedSplits = splits.filter(s => s.included);
    const includedCount = includedSplits.length;

    if (includedCount > 0) {
      if (splitMethod === 'percentage') {
        const basePercentage = Number((100 / includedCount).toFixed(2));
        let assignedPercentage = 0;

        // First, calculate assignments for all but the last person
        const updatedSplits = splits.map(split => {
          if (!split.included) {
            return { ...split, percentage: 0 };
          }

          // Find if this is the last included split
          const includedIndex = includedSplits.findIndex(
            s => s.userId === split.userId
          );
          const isLast = includedIndex === includedCount - 1;

          if (isLast) {
            // Last person gets exactly what's needed to reach 100%
            const remainder = 100 - assignedPercentage;
            return { ...split, percentage: Number(remainder.toFixed(2)) };
          }

          assignedPercentage = Number(
            (assignedPercentage + basePercentage).toFixed(2)
          );
          return { ...split, percentage: basePercentage };
        });

        setSplits(updatedSplits);
      } else if (splitMethod === 'custom') {
        const baseAmount = numericAmount / includedCount;
        let assignedAmount = 0;
        let processedCount = 0;

        const updatedSplits = splits.map(split => {
          if (!split.included) {
            return { ...split, amount: 0, amountFormatted: '0' };
          }

          processedCount++;

          // Last person gets the exact remainder to avoid rounding errors
          if (processedCount === includedCount) {
            const remainder = numericAmount - assignedAmount;
            return {
              ...split,
              amount: remainder,
              amountFormatted: roundToCurrencyPrecision(
                remainder,
                currency
              ).toFixed(getCurrencyDecimals(currency)),
            };
          }

          const roundedAmount = roundToCurrencyPrecision(baseAmount, currency);
          assignedAmount += roundedAmount;

          return {
            ...split,
            amount: roundedAmount,
            amountFormatted: roundedAmount.toFixed(
              getCurrencyDecimals(currency)
            ),
          };
        });

        setSplits(updatedSplits);
      }
    }
  };

  const handleAutoComplete = () => {
    if (splitMethod === 'percentage') {
      const totalPercentage = includedSplits.reduce(
        (sum, split) => sum + (split.percentage || 0),
        0
      );
      const remaining = 100 - totalPercentage;

      if (remaining > 0 && includedSplits.length > 0) {
        // Check if there are empty splits
        const emptySplits = includedSplits.filter(
          s => !s.percentage || s.percentage === 0
        );

        // If there are empty splits, fill only those
        // Otherwise, redistribute among ALL included splits
        const splitsToFill =
          emptySplits.length > 0 ? emptySplits : includedSplits;
        const baseAddition = Number(
          (remaining / splitsToFill.length).toFixed(2)
        );
        let assignedFromRemaining = 0;
        let processedCount = 0;

        setSplits(
          splits.map(split => {
            if (!split.included) {
              return split;
            }

            // Check if this split should receive additional percentage
            const shouldReceiveAddition =
              emptySplits.length > 0
                ? !split.percentage || split.percentage === 0
                : true;

            if (!shouldReceiveAddition) {
              return split;
            }

            processedCount++;

            // Last split gets the exact remainder to avoid rounding errors
            if (processedCount === splitsToFill.length) {
              const finalAddition = Number(
                (remaining - assignedFromRemaining).toFixed(2)
              );
              return {
                ...split,
                percentage: (split.percentage || 0) + finalAddition,
              };
            }

            assignedFromRemaining += baseAddition;
            return {
              ...split,
              percentage: (split.percentage || 0) + baseAddition,
            };
          })
        );
      }
    } else if (splitMethod === 'custom') {
      const totalAllocated = includedSplits.reduce(
        (sum, split) => sum + (split.amount || 0),
        0
      );
      const remaining = numericAmount - totalAllocated;

      if (remaining > 0 && includedSplits.length > 0) {
        // Check if there are empty splits
        const emptySplits = includedSplits.filter(
          s => !s.amount || s.amount === 0
        );

        // If there are empty splits, fill only those
        // Otherwise, redistribute among ALL included splits
        const splitsToFill =
          emptySplits.length > 0 ? emptySplits : includedSplits;
        const baseAddition = roundToCurrencyPrecision(
          remaining / splitsToFill.length,
          currency
        );
        let assignedFromRemaining = 0;
        let processedCount = 0;

        setSplits(
          splits.map(split => {
            if (!split.included) {
              return split;
            }

            // Check if this split should receive additional amount
            const shouldReceiveAddition =
              emptySplits.length > 0
                ? !split.amount || split.amount === 0
                : true;

            if (!shouldReceiveAddition) {
              return split;
            }

            processedCount++;

            // Last split gets the exact remainder to avoid rounding errors
            if (processedCount === splitsToFill.length) {
              const finalAddition = roundToCurrencyPrecision(
                remaining - assignedFromRemaining,
                currency
              );
              return {
                ...split,
                amount: (split.amount || 0) + finalAddition,
                amountFormatted: ((split.amount || 0) + finalAddition).toFixed(
                  getCurrencyDecimals(currency)
                ),
              };
            }

            assignedFromRemaining += baseAddition;
            return {
              ...split,
              amount: (split.amount || 0) + baseAddition,
              amountFormatted: ((split.amount || 0) + baseAddition).toFixed(
                getCurrencyDecimals(currency)
              ),
            };
          })
        );
      }
    }
  };

  return {
    numericAmount,
    includedSplits,
    calculationResult,
    getEqualSplitAmount,
    getSharesSplitAmount,
    getEffectiveShares,
    handleIncludeToggle,
    handlePercentageChange,
    handlePercentageSliderChange,
    handleAmountChange,
    handleSharesChange,
    handleSplitEvenly,
    handleAutoComplete,
  };
}
